import { useState, useRef } from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { CustomAutocomplete } from '../../StyledInputs';
import { CustomIconButton, DefaultContainedButton, DefaultOutlinedButton, RedOutlinedButton } from '../../Buttons';
import { usePopup } from '../../../context/PopupContext';
import { LoadingUI } from '../../LayoutStyles';
import { DecisionDialog } from '../../DecisionDialog';

import API from '../../../api';

export default function GuestsCard({ guests, isGuestsApiCalled, canEdit, refreshGuestsList, disabledButton, eventId }) {
  const { showPopup, setPopUpText } = usePopup();

  const [openDeleteGuest, setOpenDeleteGuest] = useState(false);
  const [guestToDelete, setGuestToDelete] = useState(null);

  const handleOpenDeleteGuest = (guest) => {
    setOpenDeleteGuest(true);
    setGuestToDelete(guest);
  };

  const handleCloseDeleteGuest = () => {
    setOpenDeleteGuest(false);
    setGuestToDelete(null);
  };

  const onDeleteGuest = () => {
    API.delete(`/event_guests/${guestToDelete?.id}`).then((response) => {
      handleCloseDeleteGuest();
      refreshGuestsList();
      setPopUpText(response.data.message);
      showPopup(true);
    });
  };

  const [openCreateGuest, setOpenCreateGuest] = useState(false);
  const [guestsList, setGuestsList] = useState([]);
  const [isGuestsListFetching, setIsGuestsListFetching] = useState(true);
  const [guestToAdd, setGuestToAdd] = useState({});
  const searchDebounceRef = useRef(null);

  const handleOpenCreateGuest = () => {
    setOpenCreateGuest(true);
    setGuestsList([]);
    setIsGuestsListFetching(false);
  };

  const handleCloseCreateGuest = () => {
    setOpenCreateGuest(false);
    setGuestsList([]);
  };

  const searchGuests = (search) => {
    clearTimeout(searchDebounceRef.current);
    if (search.length < 3) {
      setGuestsList([]);
      return;
    }
    searchDebounceRef.current = setTimeout(() => {
      setIsGuestsListFetching(true);
      API.get('/event_guests', { params: { search } })
        .then((response) => {
          setGuestsList(response.data);
        })
        .finally(() => setIsGuestsListFetching(false));
    }, 500);
  };

  const onCreateGuest = () => {
    const data = {
      event_id: eventId,
      user_id: guestToAdd.id,
    };

    API.post('/event_guests', data)
      .then((response) => {
        refreshGuestsList();
        setPopUpText(response.data.message);
        showPopup(true);
        handleCloseCreateGuest();
      })
      .catch((error) => {
        console.log(error);
        setPopUpText(error?.response?.data?.message || 'Si è verificato un errore');
        showPopup(false);
      });
  };

  return (
    <Card
      sx={{
        padding: '24px',
        marginBottom: '24px',
        maxHeight: '500px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ mb: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Ospiti Evento</Typography>
        {canEdit && (
          <DefaultContainedButton disabled={disabledButton} onClick={() => handleOpenCreateGuest()}>
            Aggiungi Ospite
          </DefaultContainedButton>
        )}
      </Box>

      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Nome</TableCell>
            <TableCell>Carica</TableCell>
            {canEdit && <TableCell />}
          </TableRow>
        </TableHead>
      </Table>

      <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
        <TableContainer>
          <Table>
            {isGuestsApiCalled ? (
              guests.length > 0 ? (
                <TableBody>
                  {guests.map((guest) => (
                    <TableRow key={guest.id}>
                      <TableCell component="th" scope="row">
                        {guest.name} {guest.surname}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {guest.position}
                      </TableCell>
                      {canEdit && (
                        <TableCell>
                          <Tooltip title={'Rimuovi ospite'} arrow placement="top">
                            <Box sx={{ width: 'fit-content' }}>
                              <CustomIconButton onClick={() => handleOpenDeleteGuest(guest)}>
                                {' '}
                                <DeleteIcon />
                              </CustomIconButton>
                            </Box>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              ) : (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={3} sx={{ textAlign: 'center' }}>
                      <Typography variant="span">Nessun ospite confermato</Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )
            ) : (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={8}>{LoadingUI()}</TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Box>

      <DecisionDialog
        openDialog={openDeleteGuest}
        dialogTitle="Rimozione ospite "
        dialogDescription={`Vuoi rimuovere ${guestToDelete?.name} ${guestToDelete?.surname} come ospite di questo evento?`}
        closeOperation={handleCloseDeleteGuest}
        requestOperation={() => onDeleteGuest()}
        requestText={'Elimina'}
        closeText={'Chiudi'}
        RequestButton={RedOutlinedButton}
      />

      <Dialog open={openCreateGuest} fullWidth onClose={() => {}}>
        <DialogTitle>Aggiungi Ospite</DialogTitle>
        <DialogContent>
              <CustomAutocomplete
              customStyles={{ py: 1 }}
              options={guestsList}
              loading={isGuestsListFetching}
              filterOptions={(x) => x}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => `${option.name} ${option.surname}`}
              onInputChange={(e, value, reason) => {
                if (reason === 'input') {
                  searchGuests(value);
                }
              }}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  key={option.id}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start !important',
                    textAlign: 'left',
                  }}
                >
                  <Typography variant="span">
                    {option.name} {option.surname}
                  </Typography>
                  <Typography variant="span" sx={{ color: 'text.secondary' }}>
                    {option.position}
                    <Typography sx={{ color: option.position_status === 'EXPIRED' ? 'red' : 'green' }}>
                      {option.position_status === 'EXPIRED' ? 'Carica scaduta' : 'Carica in corso'}
                    </Typography>
                  </Typography>
                </Box>
              )}
              onChange={(e, value) => {
                setGuestToAdd(value);
              }}
              noOptionsText="Digita almeno 3 caratteri per cercare"
              renderInput={(params) => <TextField {...params} label="Cerca ospite" name="guest" variant="outlined" />}
            />
        </DialogContent>
        <DialogActions>
          <DefaultOutlinedButton onClick={handleCloseCreateGuest}>Chiudi</DefaultOutlinedButton>

          <DefaultContainedButton onClick={() => onCreateGuest()}>Aggiungi</DefaultContainedButton>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
